import { PostgrestError } from '@supabase/supabase-js'
import { supabase } from './supabase'

export type RosterType = 'BLOCK' | 'FWA' | 'BLOCK_PARTIAL'
export type BlockCode = '1A' | '1B' | '2A' | '2B' | '3A' | '3B' | '4A' | '4B'
export type WeekStart = 'SUNDAY' | 'MONDAY'
export type ProfileStatus = 'PENDING' | 'ACTIVE' | 'DISABLED'

/**
 * Mirrors `public.profiles` (`supabase/migrations/0001_profiles.sql:16-30`) exactly, column
 * for column, nullability included. `week_start` is `not null default 'SUNDAY'` so it is typed
 * non-nullable; every other nullable column is typed `| null`.
 */
export interface Profile {
  id: string
  display_name: string | null
  facebook_username: string | null
  roster_type: RosterType | null
  block_code: BlockCode | null
  classification: string | null
  station: string | null
  week_start: WeekStart
  is_admin: boolean
  status: ProfileStatus
  created_at: string
  updated_at: string
}

/**
 * Exactly the column list `0001_profiles.sql`'s `GRANT UPDATE (...)` (lines 102-104) names.
 * `id`, `status`, `is_admin`, `created_at`, `updated_at` are deliberately absent: the database
 * silently ignores a client write to any of them (outside the granted column list), and
 * `profiles_guard()` additionally restores `id`/`status`/`is_admin` on any client-originated
 * update. Excluding them here turns a would-be silent no-op into a compile error instead.
 */
export type ProfileUpdate = Partial<
  Pick<
    Profile,
    | 'display_name'
    | 'facebook_username'
    | 'roster_type'
    | 'block_code'
    | 'classification'
    | 'station'
    | 'week_start'
  >
>

/**
 * Fetches one user's `profiles` row by id.
 *
 * `userId` is required and filtered with `.eq('id', userId)` explicitly, rather than relying on
 * RLS alone to narrow the result to one row. Before `0005_profiles_admin_read.sql`,
 * `profiles_select_own` (`id = auth.uid()`) made an unfiltered query equivalent to this one for
 * every caller. Now that `profiles_select_admin` lets an admin `SELECT` every row, an unfiltered
 * query from an admin matches every row in the table and `.maybeSingle()` throws a PostgREST 406
 * ("more than one row") the moment more than one profile exists — which is exactly the sole
 * admin's own first fetch, the one account this can never break. The explicit filter is what
 * makes this call always resolve to at most one row regardless of caller privilege.
 *
 * `.maybeSingle()`, not `.single()`, is still deliberate — it resolves to `{ data: null, error:
 * null }` when the row doesn't exist (a live session with no profile row yet, or a caller with no
 * read access to the given id) rather than throwing a "no rows" error, which is exactly the
 * distinction a later `gate.ts` needs to tell apart from a real query error.
 *
 * @param userId - The id of the profile row to fetch.
 * @returns A promise resolving to `{ profile, error }`; `profile` is `null` both when no row
 *          exists and when the query errored — check `error` to distinguish the two.
 */
export async function fetchProfile(userId: string): Promise<{
  profile: Profile | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()
  return { profile: data as Profile | null, error }
}

/**
 * Updates the signed-in user's own `profiles` row and returns the updated row.
 *
 * `userId` is required and filtered with `.eq('id', userId)` explicitly, for exactly the reason
 * `fetchProfile` above carries the same filter: RLS alone no longer narrows a `profiles`
 * statement to one row for every caller. Today `profiles_update_own`
 * (`0001_profiles.sql`) is the only UPDATE policy on the table, so an unfiltered update happens
 * to touch just the caller's own row — but `0005_profiles_admin_read.sql` has already made that
 * assumption false on the SELECT side, and the day an admin UPDATE policy is added (an admin
 * fixing someone's `roster_type` is on this project's own deferred list) an unfiltered update
 * from an admin would rewrite every row in the table with that admin's form values, and
 * `.maybeSingle()` would start throwing a PostgREST 406. The explicit filter is what keeps this
 * call scoped to one row regardless of caller privilege.
 *
 * The `.select()` after `.update()` is what makes PostgREST return the updated row
 * (`returning=representation`) — it depends on the `GRANT SELECT` already present in
 * `0001_profiles.sql:101`. `.maybeSingle()` for the same reason as `fetchProfile`.
 *
 * @param userId - The id of the profile row to update; the caller's own.
 * @param updates - Fields to update; limited to `ProfileUpdate`'s columns.
 * @returns A promise resolving to `{ profile, error }`; `profile` is the updated row on success.
 */
export async function updateProfile(
  userId: string,
  updates: ProfileUpdate,
): Promise<{
  profile: Profile | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .maybeSingle()
  return { profile: data as Profile | null, error }
}

export type RedeemResult =
  | 'OK'
  | 'NOT_FOUND'
  | 'EXPIRED'
  | 'REVOKED'
  | 'EXHAUSTED'
  | 'ALREADY_REDEEMED'
  | 'ALREADY_ACTIVE'
  | 'DISABLED'

/**
 * Redeems an invite code by calling `public.redeem_invite(p_code text)`
 * (`supabase/migrations/0002_invites.sql`).
 *
 * `code` is sent exactly as given — no trim/uppercase normalization here. The database
 * function already does `upper(trim(p_code))` internally, and any client-side normalization
 * is `JoinScreen`'s concern (a later task that owns `?code=` parsing), not this file's.
 *
 * `redeem_invite()` has two failure paths outside its 8-value `redeem_result` enum contract —
 * it `raise exception`s for situations that should never happen in this phase's reachable UI
 * (an unauthenticated caller; an authenticated caller with no profile row at all) rather than
 * returning a typed value. When the RPC throws, `supabase.rpc()` resolves to
 * `{ data: null, error: PostgrestError }`, which is why `result` is nullable here too.
 *
 * @param code - The invite code as entered/parsed by the caller, unmodified.
 * @returns A promise resolving to `{ result, error }`. On success, `result` is one of the 8
 *          typed `RedeemResult` values and `error` is `null`. On failure (the two exception
 *          paths above), `result` is `null` and `error` carries the `PostgrestError` — a later
 *          `JoinScreen` task is responsible for showing a fallback message in that case, which
 *          is not part of `REDEEM_MESSAGES` below.
 */
export async function redeemInvite(code: string): Promise<{
  result: RedeemResult | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.rpc('redeem_invite', { p_code: code })
  if (error) return { result: null, error }
  return { result: data as RedeemResult, error: null }
}

/**
 * Human-readable sentence for each `redeem_result` value. Typed as `Record<RedeemResult,
 * string>` so a ninth `redeem_result` value ever added to the database enum is a compile error
 * here, not a screen that silently renders nothing — the failure mode CLAUDE.md's "never show
 * an unexplained warning" rule is about.
 */
export const REDEEM_MESSAGES: Record<RedeemResult, string> = {
  OK: "You're in — setting up your profile now.",
  NOT_FOUND: "That code doesn't match anything. Check for a typo and try again.",
  EXPIRED: 'That invite code has expired. Ask whoever sent it for a new one.',
  REVOKED: 'That invite code has been revoked.',
  EXHAUSTED: 'That invite code has already been used the maximum number of times.',
  ALREADY_REDEEMED: "You've already redeemed this code — you're all set.",
  ALREADY_ACTIVE: "You're already signed in and active. No code needed.",
  DISABLED: 'Your account has been disabled. Contact an admin if you think this is a mistake.',
}
